export interface Image {
  url: string;
  alt: string;
}

export interface Floorplan {
  url: string;
  name: string;
}

export interface Apartment {
  id: string;           // Will use InterneBemerkung (e.g., W2.3)
  name: string;         // Formatted apartment name
  bedrooms: number;     // Derived from anzahl_zimmer
  bathrooms: number;    // Default to 1
  areaM2: number;       // Direct wohnflaeche value in m²
  areaSqFt: number;     // Alias for areaM2 for UI consistency
  totalRooms: number;   // Original anzahl_zimmer value
  description: string;  // Constructed from available data
  images: Image[];      // Placeholder for now
  floorplan: Floorplan | null;
  status: string;       // Direct status2 value
  isAvailable: boolean; // Derived from status
  
  // Rental-specific fields
  coldRent: number;     // kaltmiete
  utilities: number;    // nebenkosten
  pricePerMonth: number; // Total monthly cost (coldRent + utilities)
  floor: number;        // etage
  internalId: string;   // Original InterneBemerkung
}

// Generate random units within specified ranges
function generateRandomUnits(): Apartment[] {
  const units: Apartment[] = [];
  const statuses = ['AVAILABLE', 'RENTED', 'RESERVED', 'IN PREPARATION'];
  const statusAvailability = {
    'AVAILABLE': true,
    'RENTED': false,
    'RESERVED': false,
    'IN PREPARATION': false
  };
  
  // Generate W1.1 to W1.34 units
  for (let i = 1; i <= 34; i++) {
    const unitId = `W1.${i}`;
    const rooms = Math.floor(Math.random() * 3) + 2; // 2-4 rooms
    const area = Math.floor(Math.random() * 500) / 10 + 50; // 50.0-99.9 m² with decimal
    const coldRent = Math.floor(Math.random() * 400) + 700; // 700-1100€
    const utilities = Math.floor(Math.random() * 150) + 200; // 200-350€
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    
    units.push({
      id: unitId,
      name: `Apartment ${unitId}`,
      bedrooms: Math.max(1, rooms - 1),
      bathrooms: 1,
      areaM2: area,
      areaSqFt: area,
      totalRooms: rooms,
      coldRent,
      utilities,
      pricePerMonth: coldRent + utilities,
      floor: 1,
      internalId: unitId,
      description: `Apartment on floor 1, ${rooms} rooms`,
      images: [
        { url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Cdefs%3E%3Cpattern id='grid' width='20' height='20' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 20 0 L 0 0 0 20' fill='none' stroke='%23e0e8f0' stroke-width='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='400' height='300' fill='%23f8fafc' stroke='%23cbd5e1' stroke-width='2'/%3E%3Crect width='400' height='300' fill='url(%23grid)'/%3E%3Cg stroke='%23374151' stroke-width='2' fill='none'%3E%3Crect x='50' y='50' width='300' height='200'/%3E%3Cline x1='50' y1='150' x2='200' y2='150'/%3E%3Cline x1='200' y1='50' x2='200' y2='250'/%3E%3Cline x1='200' y1='200' x2='350' y2='200'/%3E%3Cline x1='280' y1='200' x2='280' y2='250'/%3E%3Crect x='60' y='60' width='20' height='8' fill='%23374151'/%3E%3Crect x='310' y='210' width='30' height='15' fill='none' stroke='%23374151' stroke-width='1'/%3E%3Ccircle cx='320' cy='220' r='3' fill='%23374151'/%3E%3Crect x='210' y='60' width='40' height='20' fill='none' stroke='%23374151' stroke-width='1'/%3E%3Cpath d='M220 80 Q230 85 240 80' stroke='%23374151' stroke-width='1' fill='none'/%3E%3C/g%3E%3Ctext x='125' y='110' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3ELiving Room%3C/text%3E%3Ctext x='125' y='190' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EKitchen%3C/text%3E%3Ctext x='275' y='130' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EBedroom%3C/text%3E%3Ctext x='315' y='235' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EBath%3C/text%3E%3Ctext x='200' y='30' text-anchor='middle' font-family='Arial, sans-serif' font-size='12' font-weight='bold' fill='%23374151'%3EFloor Plan%3C/text%3E%3C/svg%3E", alt: `${unitId} - Floor Plan` },
      ],
      floorplan: null,
      status,
      isAvailable: statusAvailability[status as keyof typeof statusAvailability]
    });
  }

  // Generate W2.1 to W2.36 units
  for (let i = 1; i <= 36; i++) {
    const unitId = `W2.${i}`;
    const rooms = Math.floor(Math.random() * 3) + 2; // 2-4 rooms
    const area = Math.floor(Math.random() * 600) / 10 + 60; // 60.0-119.9 m² with decimal
    const coldRent = Math.floor(Math.random() * 500) + 800; // 800-1300€
    const utilities = Math.floor(Math.random() * 150) + 250; // 250-400€
    // For W2.31-W2.36, ensure visible statuses only (exclude IN PREPARATION)
    const visibleStatuses = ['AVAILABLE', 'RENTED', 'RESERVED'];
    const status = i >= 31
      ? visibleStatuses[Math.floor(Math.random() * visibleStatuses.length)]
      : statuses[Math.floor(Math.random() * statuses.length)];
    
    units.push({
      id: unitId,
      name: `Apartment ${unitId}`,
      bedrooms: Math.max(1, rooms - 1),
      bathrooms: rooms >= 4 ? 2 : 1,
      areaM2: area,
      areaSqFt: area,
      totalRooms: rooms,
      coldRent,
      utilities,
      pricePerMonth: coldRent + utilities,
      floor: 2,
      internalId: unitId,
      description: `Apartment on floor 2, ${rooms} rooms`,
      images: [
        { url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Cdefs%3E%3Cpattern id='grid' width='20' height='20' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 20 0 L 0 0 0 20' fill='none' stroke='%23e0e8f0' stroke-width='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='400' height='300' fill='%23f8fafc' stroke='%23cbd5e1' stroke-width='2'/%3E%3Crect width='400' height='300' fill='url(%23grid)'/%3E%3Cg stroke='%23374151' stroke-width='2' fill='none'%3E%3Crect x='50' y='50' width='300' height='200'/%3E%3Cline x1='50' y1='150' x2='200' y2='150'/%3E%3Cline x1='200' y1='50' x2='200' y2='250'/%3E%3Cline x1='200' y1='200' x2='350' y2='200'/%3E%3Cline x1='280' y1='200' x2='280' y2='250'/%3E%3Crect x='60' y='60' width='20' height='8' fill='%23374151'/%3E%3Crect x='310' y='210' width='30' height='15' fill='none' stroke='%23374151' stroke-width='1'/%3E%3Ccircle cx='320' cy='220' r='3' fill='%23374151'/%3E%3Crect x='210' y='60' width='40' height='20' fill='none' stroke='%23374151' stroke-width='1'/%3E%3Cpath d='M220 80 Q230 85 240 80' stroke='%23374151' stroke-width='1' fill='none'/%3E%3C/g%3E%3Ctext x='125' y='110' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3ELiving Room%3C/text%3E%3Ctext x='125' y='190' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EKitchen%3C/text%3E%3Ctext x='275' y='130' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EBedroom%3C/text%3E%3Ctext x='315' y='235' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EBath%3C/text%3E%3Ctext x='200' y='30' text-anchor='middle' font-family='Arial, sans-serif' font-size='12' font-weight='bold' fill='%23374151'%3EFloor Plan%3C/text%3E%3C/svg%3E", alt: `${unitId} - Floor Plan` },
      ],
      floorplan: null,
      status,
      isAvailable: statusAvailability[status as keyof typeof statusAvailability]
    });
  }
  
  return units;
}

// Simplified fake data for testing
export const FAKE_APARTMENT_DATA: Apartment[] = generateRandomUnits();

// --- Mock API Fetch Function ---
export const fetchApartmentsAPI = async (): Promise<Apartment[]> => {
  const useFakeData = import.meta.env.VITE_USE_FAKE_APARTMENT_DATA === 'true' || 
                      import.meta.env.VITE_USE_FALLBACK === 'true';
  
  console.log('Environment variables:', {
    VITE_USE_FAKE_APARTMENT_DATA: import.meta.env.VITE_USE_FAKE_APARTMENT_DATA,
    VITE_USE_FALLBACK: import.meta.env.VITE_USE_FALLBACK,
    useFakeData
  });
  
  if (useFakeData) {
    console.log('API CALL: Using fake wohnung data...');
    await new Promise(resolve => setTimeout(resolve, 1500)); 
    return [...FAKE_APARTMENT_DATA];
  } else {
    console.log('API CALL: Fetching real wohnung data from onOffice...');
    try {
      const parentId = 10577;
      const apartments = await import('../services/onoffice').then(m => m.fetchOnOfficeApartments(parentId));
      console.log('API CALL: Successfully fetched onOffice wohnungen:', apartments.length);
      return apartments;
    } catch (error) {
      console.error('API CALL: Error fetching from onOffice:', error);
      throw error;
    }
  }
}; 